<template>
  <UiSelect
    ref="control"
    class="ui-multi-select"
    :class="{ 'uses-ellipsis-summary': ellipsisSummary && effectiveCollapseTags }"
    :value="value"
    multiple
    :collapse-tags="effectiveCollapseTags"
    v-bind="$attrs"
    v-on="forwardedListeners"
    @input="handleInput"
    @change="handleChange"
  >
    <el-option v-if="allOption" :label="$t(allLabel)" :value="allValue" />
    <el-option
      v-for="(item, index) in options"
      :key="optionKey(item, index)"
      :label="$t(optionLabel(item))"
      :value="optionValue(item)"
      :disabled="optionDisabled(item)"
    >
      <slot name="option" :option="item" :index="index">
        <span>{{ $t(optionLabel(item)) }}</span>
      </slot>
    </el-option>
  </UiSelect>
</template>

<script>
import { t } from '@/i18n'

export default {
  name: 'UiMultiSelect',
  inheritAttrs: false,
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    valueKey: { type: String, default: 'value' },
    labelKey: { type: String, default: 'label' },
    disabledKey: { type: String, default: 'disabled' },
    allOption: { type: Boolean, default: false },
    allValue: { type: [String, Number], default: '__all__' },
    allLabel: { type: String, default: '全部' },
    collapseTags: { type: Boolean, default: true },
    adaptiveCollapse: { type: Boolean, default: true },
    ellipsisSummary: { type: Boolean, default: true }
  },
  data () {
    return {
      isOverflowing: false
    }
  },
  computed: {
    effectiveCollapseTags () {
      return this.collapseTags && (!this.adaptiveCollapse || this.isOverflowing)
    },
    selectedOptionLabels () {
      return this.value.map(value => this.selectedLabel(value))
    },
    forwardedListeners () {
      const listeners = { ...this.$listeners }
      delete listeners.input
      delete listeners.change
      return listeners
    }
  },
  watch: {
    value: {
      deep: true,
      handler () { this.queueCollapseMeasure() }
    },
    options: {
      deep: true,
      handler () { this.queueCollapseMeasure() }
    },
    collapseTags () { this.queueCollapseMeasure() },
    adaptiveCollapse () { this.queueCollapseMeasure() }
  },
  mounted () {
    this.createMeasureElement()
    this.queueCollapseMeasure()
    window.addEventListener('resize', this.queueCollapseMeasure)
    if (window.ResizeObserver && this.$refs.control?.$el) {
      this.resizeObserver = new window.ResizeObserver(this.queueCollapseMeasure)
      this.resizeObserver.observe(this.$refs.control.$el)
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.queueCollapseMeasure)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame)
    if (this.measureElement?.parentNode) this.measureElement.parentNode.removeChild(this.measureElement)
  },
  methods: {
    optionValue (item) {
      return item && typeof item === 'object' ? item[this.valueKey] : item
    },
    optionLabel (item) {
      const label = item && typeof item === 'object' ? item[this.labelKey] : item
      return label == null ? '' : String(label)
    },
    optionDisabled (item) {
      return Boolean(item && typeof item === 'object' && item[this.disabledKey])
    },
    optionKey (item, index) {
      const value = this.optionValue(item)
      return value == null ? `option-${index}` : String(value)
    },
    selectedLabel (value) {
      if (this.allOption && value === this.allValue) return this.allLabel
      const option = this.options.find(item => this.optionValue(item) === value)
      return option === undefined ? String(value ?? '') : this.optionLabel(option)
    },
    createMeasureElement () {
      if (this.measureElement) return
      this.measureElement = document.createElement('div')
      this.measureElement.className = 'ui-multi-select ui-multi-select__measure'
      document.body.appendChild(this.measureElement)
    },
    queueCollapseMeasure () {
      if (!this._isMounted || this._isBeingDestroyed) return
      if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame)
      this.measureFrame = window.requestAnimationFrame(() => {
        this.measureFrame = null
        this.measureCollapseState()
      })
    },
    measureCollapseState () {
      if (!this.collapseTags || !this.adaptiveCollapse || this.value.length < 2) {
        this.isOverflowing = false
        return
      }
      this.createMeasureElement()
      const group = document.createElement('span')
      this.selectedOptionLabels.forEach(label => {
        const tag = document.createElement('span')
        tag.className = 'el-tag el-tag--info el-tag--small'
        const text = document.createElement('span')
        text.className = 'el-select__tags-text'
        text.textContent = t(label)
        const close = document.createElement('i')
        close.className = 'el-tag__close el-icon-close'
        tag.appendChild(text)
        tag.appendChild(close)
        group.appendChild(tag)
      })
      this.measureElement.replaceChildren(group)
      const controlWidth = this.$refs.control?.$el?.getBoundingClientRect().width || 0
      const availableWidth = Math.max(0, controlWidth - 34)
      const requiredWidth = this.measureElement.getBoundingClientRect().width
      this.isOverflowing = requiredWidth > availableWidth + 1
    },
    normalizeSelection (values) {
      const normalized = Array.isArray(values) ? values.filter(value => value != null) : []
      if (!this.allOption) return normalized
      if (!normalized.length) return [this.allValue]
      if (!normalized.includes(this.allValue) || normalized.length === 1) return normalized
      const latestValue = normalized[normalized.length - 1]
      return latestValue === this.allValue
        ? [this.allValue]
        : normalized.filter(value => value !== this.allValue)
    },
    handleInput (values) {
      this.$emit('input', this.normalizeSelection(values))
    },
    handleChange (values) {
      this.$emit('change', this.normalizeSelection(values))
    },
    focus () {
      if (this.$refs.control) this.$refs.control.focus()
    },
    blur () {
      if (this.$refs.control) this.$refs.control.blur()
    }
  }
}
</script>

<style lang="scss">
.ui-multi-select .el-select__tags {
  max-width: calc(100% - 34px) !important;
  overflow: hidden;
  white-space: nowrap;
}

.ui-multi-select .el-select__tags > span {
  min-width: 0;
  display: flex;
  align-items: center;
}

.ui-multi-select .el-select__tags .el-tag {
  min-width: 0;
  height: 26px;
  margin: 2px 0 2px var(--ui-space-xs);
  flex: 0 1 auto;
  color: var(--ui-text-on-bright) !important;
  border: 0 !important;
  background: var(--ui-color-info) !important;
  line-height: 24px;
}

.ui-multi-select .el-select__tags .el-tag:first-child .el-select__tags-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ui-multi-select.uses-ellipsis-summary .el-select__tags .el-tag:nth-child(2) .el-select__tags-text {
  font-size: 0;
}

.ui-multi-select.uses-ellipsis-summary .el-select__tags .el-tag:nth-child(2) .el-select__tags-text::after {
  content: '…';
  font-size: var(--ui-font-size-caption);
}

.ui-multi-select .el-select__tags .el-tag__close {
  color: var(--ui-text-on-bright) !important;
  background: rgba(var(--ui-bg-page-rgb), .14) !important;
}

@media (hover: hover) and (pointer: fine) {
  .ui-multi-select .el-select__tags .el-tag__close:hover {
    color: var(--ui-text-on-bright) !important;
    background: rgba(var(--ui-bg-page-rgb), .28) !important;
  }
}

.ui-multi-select__measure {
  width: max-content;
  position: fixed;
  top: -10000px;
  left: -10000px;
  z-index: -1;
  visibility: hidden;
  pointer-events: none;
}

.ui-multi-select__measure > span {
  width: max-content;
  display: flex;
  align-items: center;
}
</style>
